import React, { useCallback, useId, useMemo, useRef, useState } from "react";
import { Activity, TrendingDown, TrendingUp } from "lucide-react";

const VIEW_W = 640;
const VIEW_H = 190;
const PAD_TOP = 12;
const PAD_BOTTOM = 8;

const clamp01 = (v) => Math.min(1, Math.max(0, v));

const signed = (n) => (n > 0 ? `+${n}` : n < 0 ? `−${Math.abs(n)}` : "0");

/**
 * Cumulative net follower growth over the 7-day window (follows − unfollows,
 * running total). Green area/line while net positive, red when negative,
 * with a hover crosshair + per-day tooltip.
 */
const FollowerNetGrowthChart = ({ series, compact = false }) => {
  const gradId = useId();
  const surfaceRef = useRef(null);
  const [hoverIndex, setHoverIndex] = useState(null);

  const n = series.length;
  const hasActivity = series.some((p) => p.follows > 0 || p.unfollows > 0);
  const final = n ? series[n - 1].cumulative : 0;
  const tone = final > 0 ? "up" : final < 0 ? "down" : "flat";

  const { yMin, yMax } = useMemo(() => {
    let lo = 0;
    let hi = 0;
    for (const p of series) {
      lo = Math.min(lo, p.cumulative);
      hi = Math.max(hi, p.cumulative);
    }
    if (lo === 0 && hi === 0) hi = 1;
    const pad = (hi - lo) * 0.12;
    return { yMin: lo === 0 ? 0 : lo - pad, yMax: hi === 0 ? 0 : hi + pad };
  }, [series]);

  const innerH = VIEW_H - PAD_TOP - PAD_BOTTOM;
  const span = Math.max(yMax - yMin, 1e-9);
  const toY = useCallback(
    (v) => PAD_TOP + (1 - (v - yMin) / span) * innerH,
    [yMin, span, innerH]
  );
  const zeroY = Math.min(Math.max(toY(0), PAD_TOP), VIEW_H - PAD_BOTTOM);
  const slotW = n > 0 ? VIEW_W / n : VIEW_W;
  const centerX = useCallback((i) => (i + 0.5) * slotW, [slotW]);

  const { linePath, areaPath } = useMemo(() => {
    if (!n) return { linePath: "", areaPath: "" };
    const pts =
      n === 1
        ? [
            { x: 0, y: toY(series[0].cumulative) },
            { x: VIEW_W, y: toY(series[0].cumulative) },
          ]
        : series.map((p, i) => ({ x: centerX(i), y: toY(p.cumulative) }));
    let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
    for (let i = 1; i < pts.length; i += 1) {
      d += ` L ${pts[i].x.toFixed(1)} ${pts[i].y.toFixed(1)}`;
    }
    return {
      linePath: d,
      areaPath: `${d} L ${pts[pts.length - 1].x.toFixed(1)} ${zeroY.toFixed(1)} L ${pts[0].x.toFixed(1)} ${zeroY.toFixed(1)} Z`,
    };
  }, [n, series, centerX, toY, zeroY]);

  const handleMove = (event) => {
    if (!n || !surfaceRef.current) return;
    const rect = surfaceRef.current.getBoundingClientRect();
    const frac = clamp01((event.clientX - rect.left) / rect.width);
    setHoverIndex(Math.min(n - 1, Math.floor(frac * n)));
  };

  const hover = hoverIndex != null ? series[hoverIndex] : null;
  const hoverXFrac = hoverIndex != null ? (hoverIndex + 0.5) / n : 0;

  const toneText = tone === "up" ? "aud-up" : tone === "down" ? "aud-down" : "aud-flat";
  const HeadIcon = tone === "down" ? TrendingDown : TrendingUp;

  return (
    <section
      className={`aud-card${compact ? " aud-card-compact" : ""}`}
      aria-label="Net follower growth"
    >
      <div className="aud-ng-head">
        <div>
          <span className="aud-kicker">Net growth · 7D</span>
          {!compact ? (
            <p className="aud-caption">Follows minus unfollows, running total across the window</p>
          ) : null}
        </div>
        <div className="aud-ng-figure">
          <strong className={`tabular-nums ${toneText}`}>
            <HeadIcon size={compact ? 10 : 13} style={{ verticalAlign: "-2px", marginRight: 3 }} />
            {signed(final)}
          </strong>
          {!compact ? <span>net in window</span> : null}
        </div>
      </div>

      {!n || !hasActivity ? (
        <div className="aud-ng-empty">
          {!compact ? <Activity size={15} /> : null}
          <span>No follow activity in the last 7 days.</span>
        </div>
      ) : (
        <div
          ref={surfaceRef}
          className="aud-ng-surface"
          onPointerMove={handleMove}
          onPointerLeave={() => setHoverIndex(null)}
        >
          <svg
            viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
            preserveAspectRatio="none"
            className={`aud-ng-svg${compact ? " aud-ng-svg-compact" : ""}`}
            role="img"
            aria-label="Cumulative net followers over the last seven days"
          >
            <defs>
              <linearGradient id={`${gradId}-up`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#46c479" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#46c479" stopOpacity="0.01" />
              </linearGradient>
              <linearGradient id={`${gradId}-down`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ef5350" stopOpacity="0.01" />
                <stop offset="100%" stopColor="#ef5350" stopOpacity="0.26" />
              </linearGradient>
              <linearGradient id={`${gradId}-flat`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#64b5f6" stopOpacity="0.25" />
                <stop offset="100%" stopColor="#1e88e5" stopOpacity="0.01" />
              </linearGradient>
            </defs>

            {[yMax, (yMax + yMin) / 2, yMin].map((level, i) => (
              <line
                key={`g-${i}`}
                x1={0}
                x2={VIEW_W}
                y1={toY(level)}
                y2={toY(level)}
                className="aud-ng-grid"
                vectorEffect="non-scaling-stroke"
              />
            ))}
            {yMin < 0 ? (
              <line
                x1={0}
                x2={VIEW_W}
                y1={zeroY}
                y2={zeroY}
                className="aud-ng-grid aud-ng-grid-zero"
                vectorEffect="non-scaling-stroke"
              />
            ) : null}

            <path d={areaPath} fill={`url(#${gradId}-${tone})`} />
            <path
              d={linePath}
              className={`aud-ng-line aud-ng-line-${tone}`}
              fill="none"
              vectorEffect="non-scaling-stroke"
            />

            {hover ? (
              <line
                x1={hoverXFrac * VIEW_W}
                x2={hoverXFrac * VIEW_W}
                y1={PAD_TOP}
                y2={VIEW_H - PAD_BOTTOM}
                className="aud-ng-crosshair"
                vectorEffect="non-scaling-stroke"
              />
            ) : null}
          </svg>

          <div className="aud-ng-xaxis" aria-hidden>
            {series.map((p, i) => (
              <span key={p.date || i} style={{ left: `${((i + 0.5) / n) * 100}%` }}>
                {p.label}
              </span>
            ))}
          </div>

          {hover ? (
            <div
              className={`aud-ng-tooltip tabular-nums${hoverXFrac > 0.7 ? " aud-ng-tooltip-left" : ""}`}
              style={{ left: `${hoverXFrac * 100}%` }}
            >
              <em>{hover.label}</em>
              <span className="aud-up">+{hover.follows} follows</span>
              <span className="aud-down">−{hover.unfollows} unfollows</span>
              <strong
                className={
                  hover.cumulative > 0 ? "aud-up" : hover.cumulative < 0 ? "aud-down" : "aud-flat"
                }
              >
                {signed(hover.cumulative)} running
              </strong>
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
};

export default FollowerNetGrowthChart;
